import {
  type default as React,
  type ChangeEvent,
  type ComponentProps,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  useCallback,
  useState,
} from 'react';
import { cn, type CurrencyCode } from '@dinewise/utils';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { useControlField, useField } from '@michaelgmcd/rvf';
import PhoneInput, { type Value } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { getCurrencySymbolInputPadding, resolveCurrencyFieldOptions } from './currency-field.ts';
import { HiddenInput } from './hidden-input.tsx';

type InputPropsWithoutClassName = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'className'
>;

export type InputComponentProps = InputPropsWithoutClassName & {
  name: string;
  label?: string | React.ReactNode;
  labelPosition?: 'default' | 'floating' | 'above';
  description?: string;
  leftAccessory?: React.ReactNode;
  rightAccessory?: React.ReactNode;
  classNames?: {
    group?: string;
    checkboxContainer?: string;
    radioContainer?: string;
    input?: string;
    inputPaddingLeft?: string;
    errorInput?: string;
    errorIcon?: string;
    errorDescription?: string;
    leftAccessory?: string;
    rightAccessory?: string;
    description?: string;
    label?: string;
  };
};

export const Checkbox = (props: InputComponentProps & { error?: string }) => {
  const { classNames, defaultChecked, error, label, description, disabled, ...inputProps } = props;

  return (
    <div
      className={cn('group flex flex-col items-start', classNames?.group, error && 'input-error')}
    >
      <div
        className={cn(
          'relative flex gap-2.5 rounded-lg border border-transparent p-2 has-[:focus-visible]:border-ande-black group-[.input-error]:has-[:focus-visible]:border-error-800',
          classNames?.checkboxContainer,
        )}
      >
        <div className="relative flex items-center justify-center">
          <input
            type="checkbox"
            {...inputProps}
            {...{ disabled }}
            {...(defaultChecked === undefined ? {} : { defaultChecked })} // passing defaultChecked here instead of to `getInputProps` due to bug: https://github.com/airjp73/remix-validated-form/issues/315
            className={cn(
              "before:-translate-y-1/2 before:-translate-x-1/2 size-5 rounded border-ande-black text-ande-black shadow-xl transition-opacity [accent-color:theme(colors.ande-black)] before:absolute before:top-1/2 before:left-1/2 before:block before:h-9 before:w-9 before:rounded-full before:bg-ande-gray-500 before:opacity-0 before:transition-opacity before:content-[''] focus:outline-none focus:ring-0 focus:ring-transparent focus-visible:outline-none active:enabled:before:opacity-20 disabled:border-ande-gray-300 disabled:text-ande-gray-500 group-[.input-error]:border-ande-error-dark sm:hover:enabled:before:opacity-10",
              classNames?.input,
            )}
          />
        </div>
        {label ? (
          <label
            htmlFor={props.name}
            className={cn(
              'flex items-center text-ande-black text-sm group-[.input-error]:text-ande-gray-500',
              disabled && 'text-ande-gray-500',
              classNames?.label,
            )}
          >
            {label}
          </label>
        ) : null}
        {description ? (
          <p
            className="text-ande-gray-500 text-sm group-[.input-error]:text-ande-gray-500"
            id={`${props.name}-description`}
          >
            {description}
          </p>
        ) : null}
      </div>
      {error ? (
        <p
          className={cn('py-0.5 pl-2 text-[10px] text-ande-error', classNames?.errorDescription)}
          id={`${props.name}-error`}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};

export const Radio = (props: InputComponentProps) => {
  const { classNames, label, description, defaultChecked, ...inputProps } = props;

  return (
    <div className={cn('flex flex-col gap-2', classNames?.group)}>
      {label ? (
        <label
          htmlFor={props.id ?? props.name}
          className={cn('block pb-1 font-medium text-ande-black text-sm', classNames?.label)}
        >
          {label}
        </label>
      ) : null}
      <div className="flex flex-1 flex-col justify-center">
        <input
          {...inputProps}
          {...(defaultChecked === undefined ? {} : { defaultChecked })} // passing defaultChecked here instead of to `getInputProps` due to bug: https://github.com/airjp73/remix-validated-form/issues/315
          className={cn(
            'size-6 rounded-full text-ande-gray-500 ring-0 focus:ring-0',
            classNames?.radioContainer,
          )}
        />
      </div>
      {description ? (
        <p className="px-1 pt-2 text-ande-gray-500 text-sm" id={`${props.name}-description`}>
          {description}
        </p>
      ) : null}
    </div>
  );
};

export const Input = (props: InputComponentProps) => {
  const {
    classNames,
    leftAccessory,
    rightAccessory,
    label,
    description,
    labelPosition = 'default',
    defaultValue,
    defaultChecked,
    ...inputProps
  } = props;
  const { type, onChange: onChangeProp } = inputProps;
  const [showRightAccessory, setShowRightAccessory] = useState(true);

  const { error, getInputProps } = useField(props.name);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (type === 'search') {
        setShowRightAccessory(event.target.value.length === 0);
      }

      if (onChangeProp) {
        onChangeProp(event);
      }
    },
    [onChangeProp, type],
  );

  const id = props.id ?? props.name;

  if (props.type === 'hidden') {
    return <HiddenInput {...props} />;
  }

  if (props.type === 'checkbox') {
    return <Checkbox {...getInputProps({ ...props, id })} {...{ error, defaultChecked }} />;
  }

  if (props.type === 'radio') {
    return <Radio {...getInputProps({ ...props, id })} defaultChecked={defaultChecked} />;
  }

  const errorInputClassName =
    classNames?.errorInput ??
    'border-red-300 pr-10 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500';

  return (
    <div className={cn(classNames?.group, error && 'group error')}>
      {label && labelPosition === 'default' ? (
        <label
          htmlFor={props.name}
          className={cn('block pb-1 font-medium text-ande-gray-500 text-sm', classNames?.label)}
        >
          {label}
        </label>
      ) : null}

      <div className="relative rounded-md">
        {leftAccessory ? (
          <div
            className={cn(
              classNames?.leftAccessory ??
                'pointer-events-none absolute inset-y-0 left-0 flex items-center',
            )}
          >
            {leftAccessory}
          </div>
        ) : null}
        <input
          type="text"
          {...getInputProps({ ...inputProps, onChange, id })}
          {...(defaultValue === undefined ? {} : { defaultValue })} // passing defaultValue here instead of to `getInputProps` due to bug: https://github.com/airjp73/remix-validated-form/issues/315
          className={cn(
            'block w-full rounded-md sm:text-base',
            leftAccessory && !classNames?.inputPaddingLeft && 'pl-6',
            rightAccessory && showRightAccessory && 'pr-6',
            classNames?.input ??
              'border-ande-gray-300 focus:border-ande-gray-500 focus:outline-none focus:ring-ande-gray-500',
            !!error && errorInputClassName,
            label && labelPosition === 'floating' && 'peer',
            leftAccessory ? classNames?.inputPaddingLeft : undefined,
          )}
          aria-invalid={!!error}
          aria-describedby={error ? `${props.name}-error` : `${props.name}-description`}
        />
        {label && labelPosition !== 'default' ? (
          <label
            htmlFor={props.name}
            className={cn(
              'absolute top-1 transform cursor-text bg-ande-white px-2 text-ande-black',
              labelPosition === 'floating' &&
                '-translate-y-4 peer-focus:-translate-y-4 peer-placeholder-shown:-translate-y-1/2 absolute left-1 z-10 origin-[0] scale-75 transform px-2 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:scale-100 peer-focus:top-1 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-gray-800',
              labelPosition === 'above' &&
                '-translate-y-7 peer-focus:-translate-y-4 peer-placeholder-shown:-translate-y-1/2 left-0 origin-[0] scale-75 bg-transparent px-1 text-xs duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-gray-800',
              classNames?.label,
            )}
          >
            {label}
          </label>
        ) : null}
        {showRightAccessory && rightAccessory && !error ? (
          <div
            className={cn(
              classNames?.rightAccessory ??
                'pointer-events-none absolute inset-y-0 right-0 flex items-center p-2',
            )}
          >
            {rightAccessory}
          </div>
        ) : null}
        {error ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className={cn('size-5 text-ande-error', classNames?.errorIcon)}
              aria-hidden="true"
            />
          </div>
        ) : null}
      </div>
      {error ? (
        <p
          className={cn('px-1 py-2 text-ande-error text-sm', classNames?.errorDescription)}
          id={`${props.name}-error`}
        >
          {error}
        </p>
      ) : null}
      {!error && !!description ? (
        <p
          className={cn('px-1 pt-2 text-ande-gray-500 text-sm', classNames?.description)}
          id={`${props.name}-description`}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
};

export type InputProps = ComponentProps<typeof Input>;

type PhoneCustomInputProps = InputPropsWithoutClassName & {
  id: string;
  error?: string;
};

const PhoneCustomInput = (phoneProps: PhoneCustomInputProps) => {
  const { error, name, ...inputProps } = phoneProps;
  const inputClassName =
    'border-ande-gray-300 focus:border-ande-gray-500 focus:outline-none focus:ring-ande-gray-500';

  return (
    <input
      {...inputProps}
      className={cn('block w-full rounded-md sm:text-base', inputClassName)}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : `${name}-description`}
    />
  );
};

type PhoneNumberInputProps = InputPropsWithoutClassName & {
  name: string;
  label?: string;
  description?: string;
  classNames?: {
    group?: string;
    errorDescription?: string;
    description?: string;
  };
  onChange?: (phoneNumber: Value) => void;
};

export const PhoneNumberInput = (props: PhoneNumberInputProps) => {
  const {
    id: inputId,
    label,
    name,
    classNames,
    description,
    placeholder,
    onChange,
    ...inputProps
  } = props;
  const id = inputId ?? name;
  const { getInputProps, validate, clearError, error } = useField(name);
  const [value, setValue] = useControlField<Value>(name);

  return (
    <div className={classNames?.group}>
      {label ? (
        <label htmlFor={props.name} className="block pb-1 font-medium text-ande-gray-500 text-sm">
          {label}
        </label>
      ) : null}
      <input
        {...getInputProps({ ...inputProps, value, type: 'hidden', hidden: true })}
        id={`${id}-hidden-input`}
      />
      <PhoneInput
        {...{ placeholder, value }}
        numberInputProps={{ id }}
        inputComponent={PhoneCustomInput}
        defaultCountry="US"
        onChange={phoneNumber => {
          const number = phoneNumber ?? ('' as Value);
          setValue(number);
          onChange?.(number);
        }}
        onFocus={() => {
          clearError();
        }}
        onBlur={() => {
          validate();
        }}
      />
      {error ? (
        <p
          id={`${name}-error`}
          className={cn('p-1 text-ande-error text-xs', classNames?.errorDescription)}
        >
          {error}
        </p>
      ) : null}
      {!error && !!description ? (
        <p
          id={`${name}-description`}
          className={cn('p-1 text-ande-gray-500 text-xs', classNames?.description)}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
};

type DecimalInputProps = Omit<InputProps, 'type'> & {
  decimals?: number;
};

export const DecimalInput = (props: DecimalInputProps) => {
  const { onChange, onBlur, step, decimals = 2, min, placeholder, ...inputProps } = props;

  return (
    <Input
      {...inputProps}
      type="text"
      pattern="\d+\.?\d*"
      placeholder={placeholder ?? (decimals ? `0.${'0'.repeat(decimals)}` : '0')}
      onChange={e => {
        // ensure positive numbers and strip commas
        e.target.value = e.target.value.replace(/[-,]/g, '');
        onChange?.(e);
      }}
      onBlur={e => {
        // Ensure commas are removed before parsing
        const value = Number.parseFloat(e.target.value.replace(/,/g, ''));
        const valueToSet = Number.isNaN(value) ? '' : value.toFixed(decimals);
        e.target.value = valueToSet;
        onBlur?.(e);
      }}
    />
  );
};

type CurrencyInputProps = DecimalInputProps & {
  currencyCode?: CurrencyCode;
};

export const CurrencyInput = ({
  currencyCode = 'USD',
  decimals,
  leftAccessory,
  classNames,
  ...props
}: CurrencyInputProps) => {
  const currencyField = resolveCurrencyFieldOptions(currencyCode, decimals);
  const showDefaultSymbol = leftAccessory === undefined || leftAccessory === null;
  const resolvedLeftAccessory = showDefaultSymbol ? (
    <span className="pl-3 text-sm group-[.error]:text-ande-error-light">
      {currencyField.currencySymbol}
    </span>
  ) : (
    leftAccessory
  );
  const resolvedClassNames = showDefaultSymbol
    ? {
        inputPaddingLeft: getCurrencySymbolInputPadding(currencyField.currencySymbol),
        ...classNames,
      }
    : classNames;

  return (
    <DecimalInput
      {...props}
      decimals={currencyField.decimals}
      leftAccessory={resolvedLeftAccessory}
      classNames={resolvedClassNames}
    />
  );
};

export const PercentInput = (props: DecimalInputProps) => {
  return (
    <DecimalInput
      {...props}
      rightAccessory={<span className="pr-2 group-[.error]:text-ande-error-light">%</span>}
    />
  );
};
